/* eslint-disable no-unused-vars */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _delete, _get, _post, _put } from "../../Services/baseApi";
import { errorHandler, successToast } from "../../Utils/toast";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import {
  ROLE_ADD_URL,
  ROLE_LIST_URL,
  ROLE_DETAILS_URL,
  ROLE_UPDATE_URL,
  ROLE_DELETE_URL,
  PERMISSION_ADD_URL,
  PERMISSION_LIST_URL,
  PERMISSION_UPDATE_URL,
  PERMISSION_DELETE_URL,
  PERMISSION_AND_ROLE_LIST_URL,
} from "../../Services/endpoints";

const initialState = {
  isLoading: false,
  rolesList: {
    findRoles: [],
    limit: 0,
    page: 0,
    totalCount: 0,
  },

  permissionList: {
    findPermissions: [],
    limit: 0,
    page: 0,
    totalCount: 0,
  },
  permissionAndRoleList: [],
  roleDetails: {},
};

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    roleDetailsClean: (state) => {
      state.roleDetails = {};
    },
  },
  extraReducers: (builder) => {
    // GET ROLES
    builder
      .addCase(getRolesThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRolesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rolesList = action.payload;
      })
      .addCase(getRolesThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // GET ROLE DETAILS
    builder
      .addCase(getRoleDetailsThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRoleDetailsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roleDetails = action.payload;
      })
      .addCase(getRoleDetailsThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // ADD ROLE
    builder
      .addCase(addRolesThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addRolesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addRolesThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // UPDATE ROLE
    builder
      .addCase(updateRolesThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateRolesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateRolesThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // DELETE ROLE
    builder
      .addCase(deleteRolesThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteRolesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(deleteRolesThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // GET PERMISSIONS
    builder
      .addCase(getPermissionThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPermissionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissionList = action.payload;
      })
      .addCase(getPermissionThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // ADD PERMISSION
    builder
      .addCase(addPermissionThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addPermissionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addPermissionThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // UPDATE PERMISSION
    builder
      .addCase(updatePermissionThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePermissionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updatePermissionThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // DELETE PERMISSION
    builder
      .addCase(deletePermissionThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePermissionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(deletePermissionThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // GET PERMISSION AND ROLE LIST
    builder
      .addCase(getPermissionAndRoleListThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPermissionAndRoleListThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissionAndRoleList = action.payload;
      })
      .addCase(getPermissionAndRoleListThunk.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { roleDetailsClean } = rolesSlice.actions;
// export default rolesSlice.reducer;

// Define the reducer export with persist data
export default persistReducer(
  {
    key: "roles/persist",
    storage,
    whitelist: [],
  },
  rolesSlice.reducer
);

export const getRolesThunk = createAsyncThunk(
  "rolesSlice/getRolesThunk",
  async (payload) => {
    try {
      const response = await _get(ROLE_LIST_URL, {
        type: payload?.type || undefined,
        page: payload?.page || undefined,
        limit: payload?.limit || undefined,
        name: payload?.name || undefined,
        key: payload?.key ? payload?.key : undefined,
        keyOrder:
          payload?.key && payload?.keyOrder ? payload?.keyOrder : undefined,
      });
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const getRoleDetailsThunk = createAsyncThunk(
  "rolesSlice/getRoleDetails",
  async (payload) => {
    try {
      const response = await _get(`${ROLE_DETAILS_URL}/${payload?.id}`);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const addRolesThunk = createAsyncThunk(
  "rolesSlice/addRolesThunk",
  async (payload) => {
    try {
      const response = await _post(ROLE_ADD_URL, {
        type: payload?.type,
        name: payload?.name,
        slug: payload?.slug,
        level: payload?.level,
        permissions: payload?.permissions,
      });
      payload?.handleFun();
      successToast(response?.data?.message || "Role Created");
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const updateRolesThunk = createAsyncThunk(
  "rolesSlice/updateRolesThunk",
  async (payload) => {
    try {
      const body = { id: payload?.id };
      if (payload?.name !== undefined) body.name = payload.name;
      if (payload?.slug !== undefined) body.slug = payload.slug;
      if (payload?.type !== undefined) body.type = payload.type;
      if (payload?.level !== undefined) body.level = payload.level;
      if (payload?.permissions !== undefined) body.permissions = payload.permissions;

      const response = await _put(ROLE_UPDATE_URL, body);
      successToast(response?.data?.message || "Role Updated");
      payload?.handleFun();
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const deleteRolesThunk = createAsyncThunk(
  "rolesSlice/deleteRolesThunk",
  async (payload) => {
    try {
      const response = await _delete(`${ROLE_DELETE_URL}/${payload?.id}`);
      payload?.handleFun();
      successToast(response?.data?.message);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const getPermissionThunk = createAsyncThunk(
  "rolesSlice/getPermissionThunk",
  async (payload) => {
    try {
      const response = await _get(PERMISSION_LIST_URL, {
        page: payload?.page || undefined,
        limit: payload?.limit || undefined,
        name: payload?.name || undefined,
        key: payload?.key ? payload?.key : undefined,
        keyOrder:
          payload?.key && payload?.keyOrder ? payload?.keyOrder : undefined,
      });
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const addPermissionThunk = createAsyncThunk(
  "rolesSlice/addPermissionThunk",
  async (payload) => {
    try {
      const response = await _post(PERMISSION_ADD_URL, {
        moduleName: payload?.moduleName,
        name: payload?.name,
        slug: payload?.slug,
      });
      payload?.handleFun();
      successToast(response?.data?.message || "Permission Created");

      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const updatePermissionThunk = createAsyncThunk(
  "rolesSlice/updatePermissionThunk",
  async (payload) => {
    try {
      const response = await _put(PERMISSION_UPDATE_URL, {
        id: payload?.id,
        moduleName: payload?.moduleName,
        name: payload?.name,
        slug: payload?.slug,
      });
      payload?.handleFun();
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const deletePermissionThunk = createAsyncThunk(
  "rolesSlice/deletePermissionThunk",
  async (payload) => {
    try {
      const response = await _delete(`${PERMISSION_DELETE_URL}/${payload?.id}`);
      payload?.handleFun();
      successToast(response?.data?.message);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const getPermissionAndRoleListThunk = createAsyncThunk(
  "rolesSlice/getPermissionAndRoleListThunk",
  async (payload) => {
    try {
      const response = await _get(PERMISSION_AND_ROLE_LIST_URL);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);
